import React from 'react';

import type { Match } from '@confluence/route-manager';
import { Screen } from '@confluence/screen';
import { MainLayout } from '@confluence/main-layout';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';

const routeWithSpaceNavMapping = new Map();

// export for testing
export function RouteWithSpaceNav(match: Match) {
	const RouteWithNavParams = routeWithSpaceNavMapping.get(match.name);
	if (!RouteWithNavParams) {
		if (process.env.NODE_ENV !== 'production') {
			throw new Error(
				`Route get matched with ${match.name} but cannot find the corresponding RouteWithNavParams`,
			);
		}
		// Check here is used for catching the error in development mode, never happen in production
		return null;
	}

	const { SPACE_NAV_PARAMS } = RouteWithNavParams;
	if (typeof SPACE_NAV_PARAMS !== 'function') {
		if (process.env.NODE_ENV !== 'production') {
			throw new Error(
				`routeEntryWithNav can only be used with component with a static SPACE_NAV_PARAMS function`,
			);
		}
		// Check here is used for catching the error in development mode, never happen in production
		return null;
	}

	const isMobile = shouldShowMobileWeb();
	const spaceNavParams = SPACE_NAV_PARAMS(match);
	if (process.env.NODE_ENV !== 'production') {
		if (typeof spaceNavParams.Screen?.screenEvent !== 'object') {
			throw new Error(`SPACE_NAV_PARAMS.Screen.screenEvent should be an object`);
		}
		if (spaceNavParams.Screen?.pageState && typeof spaceNavParams.Screen.pageState !== 'object') {
			throw new Error(`SPACE_NAV_PARAMS.Screen.pageState should be an object if it is provided`);
		}
	}

	return (
		<Screen {...spaceNavParams.Screen}>
			<MainLayout enableNavigation={!isMobile} {...spaceNavParams.MainLayout}>
				{React.createElement(RouteWithNavParams, match)}
			</MainLayout>
		</Screen>
	);
}

export function routeEntryWithNav(
	routeName: string,
	RouteWithNavParams: ((match: Match) => JSX.Element | null) & {
		SPACE_NAV_PARAMS: (match: Match) => {
			Screen: any;
			MainLayout: any;
		};
	},
): { [routeName: string]: (match: Match) => JSX.Element | null } {
	routeWithSpaceNavMapping.set(routeName, RouteWithNavParams);
	// We need to ensure all the entries calling routeEntryWithNav are returning the same RouteWithSpaceNav instance
	// So that they won't get re-mounted when switching between routes
	return { [routeName]: RouteWithSpaceNav };
}
